import { forEach } from 'lodash';
import { formatValue } from '../../ksExporterUtils';
import { KSTransaction, KSstats, Priority } from './types';

export const evalOutliers = (
  transactions: KSTransaction[],
  tmpTransactions: KSTransaction[],
  stats: KSstats,
  strict: boolean = true
): { ids: number[]; transactions: KSTransaction[] } => {
  const { boundaries } = stats;
  const outlierIds: number[] = [];
  const outlierTransactions: KSTransaction[] = [];

  const calculatePriority = (amount: number, upperBound: number, lowerBound: number, mean: number): Priority => {
    // Calculate how many standard deviations away from the mean
    const stdDevsAway = Math.abs(amount - mean) / stats.stdDev;

    // Calculate percentage deviation from bounds
    const upperDeviation = (amount - upperBound) / upperBound;
    const lowerDeviation = (lowerBound - amount) / Math.abs(lowerBound);
    const maxDeviation = Math.max(upperDeviation, lowerDeviation);

    if (stdDevsAway > 5 || maxDeviation > 0.5) return 'high';
    if (stdDevsAway > 4 || maxDeviation > 0.3) return 'medium';
    return 'low';
  };

  const calculateConfidence = (amount: number, upperBound: number, lowerBound: number): number => {
    // Base confidence on multiple factors
    let confidence = 0;

    // Factor 1: How far outside the bounds (0-40 points)
    const deviation = Math.min(
      Math.abs((amount - upperBound) / upperBound),
      Math.abs((amount - lowerBound) / Math.abs(lowerBound))
    );
    confidence += Math.min(40, deviation * 100);

    // Factor 2: Sample size consideration (0-30 points)
    confidence += Math.min(30, (stats.count / 10) * 10);

    // Factor 3: Standard deviation stability (0-30 points)
    const cvScore = (stats.stdDev / stats.mean) * 100;
    confidence += Math.max(0, Math.min(30, (100 - cvScore) / 2));

    return Math.min(100, confidence);
  };

  const reasonMessage = (upperBound: number, lowerBound: number, amount: number) => {
    return `Amount ($${formatValue(amount, 2)}) is outside the range of ${formatValue(lowerBound, 2)} to ${formatValue(upperBound, 2)}
      ($${formatValue(stats.mean, 2)} +/- 3 * ${formatValue(stats.stdDev, 2)})`;
  };

  const markOutlier = (amount: number, id: number) => {
    const maxbound = strict
      ? Math.max(boundaries.threeStdDev.upper, boundaries.iqr.upper)
      : boundaries.threeStdDev.upper;
    const minbound = strict
      ? Math.min(boundaries.threeStdDev.lower, boundaries.iqr.lower)
      : boundaries.threeStdDev.lower;

    if (amount > maxbound || amount < minbound) {
      outlierIds.push(id);

      const priority = calculatePriority(amount, maxbound, minbound, stats.mean);
      const confidence = calculateConfidence(amount, maxbound, minbound);

      transactions[id].audit = {
        status: 'Outlier',
        amount: amount,
        reason: reasonMessage(maxbound, minbound, amount),
        priority,
        confidence,
      };
      outlierTransactions.push(transactions[id]);
    }
  };

  forEach(tmpTransactions, (transaction: KSTransaction) => {
    markOutlier(transaction.amount, transaction.id);
  });
  return { ids: outlierIds, transactions: outlierTransactions };
};
